"use strict";

import "./adblock";
import { doUnlockLevelRequirementHooks, rewrite } from "./hooks";
import "./cookies";
import "./objects";
import "./mods";
import "./settings";
import { rememberOriginalMapData } from "./mapStore";

type ServerBucket = {
  order?: number;
  name: string;
  players: number;
  games: Krunker.ServerTuple[];
};
type ServerBuckets = Record<string, ServerBucket>;
type ElementInnerHTMLDescriptor = PropertyDescriptor & {
  get?: () => string;
  set: (value: string) => void;
};
type RewriteableHistoryUrl = string | URL | null | undefined;
type FetchInput = Parameters<typeof window.fetch>[0];
type FetchInit = Parameters<typeof window.fetch>[1];
type AnyRecord = Record<string, any>;

delete window.require;
delete window.module;
delete window.exports;
delete window.__dirname;
delete window.__filename;

localStorage.ftue_bm = 3;

window.grecaptcha = {
  execute() {
    return Promise.resolve(
      "recaptcha-token-" + Math.random().toString(36).slice(2),
    );
  },
  ready(cb: () => void) {
    cb();
  },
  enterprise: {
    execute() {
      return Promise.resolve(
        "recaptcha-token-" + Math.random().toString(36).slice(2),
      );
    },
    ready(cb: () => void) {
      cb();
    },
  },
};

{
  const pr =
    typeof CanvasRenderingContext2D !== "undefined" &&
    CanvasRenderingContext2D.prototype;
  if (pr && pr.drawImage) {
    const drawImage = pr.drawImage;
    pr.drawImage = function (
      this: CanvasRenderingContext2D,
      ...args: Parameters<CanvasRenderingContext2D["drawImage"]>
    ): void {
      try {
        return drawImage.apply(this, args as any);
      } catch (err) {
        if (err instanceof DOMException && err.name === "InvalidStateError")
          return;
        throw err;
      }
    } as CanvasRenderingContext2D["drawImage"];
  }
}

{
  const p = Element.prototype;
  const desc = Object.getOwnPropertyDescriptor(p, "innerHTML") as
    | ElementInnerHTMLDescriptor
    | undefined;
  const replacements = [
    [
      "//client2.krunker.io/setup.exe",
      "https://krunker.zip/glorp/Krunker.io_Client_Setup_v3.1.1.exe",
    ],
    ["//client2.krunker.io/setup.dmg", "#"],
    [
      "//client2.krunker.io/setup.AppImage",
      "https://krunker.zip/glorp/Krunker.io_Client_v3.1.1.AppImage",
    ],
  ];
  if (desc && desc.set) {
    Object.defineProperty(p, "innerHTML", {
      configurable: true,
      enumerable: desc.enumerable,
      get: desc.get,
      set(value) {
        if (typeof value === "string") {
          for (const [pat, sub] of replacements)
            value = value.replace(pat, sub);
        }
        desc.set.call(this, value);
      },
    });
  }
}

// The compiled engine's `quickJoinRegion` (src/index.js:7982) delegates to
// `windows[1].getServers(hj)`, whose region-mode branch filters lobbies with
// `hM[2] > 0` — i.e. games must already have at least one player. On
// krunker.io that's always true; on a fresh self-hosted brain every lobby
// starts empty so the filter returns [] and clicking Quick Join is a no-op.
// Replace the function with one that mirrors getServers' bucket/order logic
// but drops the populated-only filter.
function quickJoinRegion(hj: number): void {
  const ww = window.windows?.[1];
  const servers: ServerBuckets = {
    custom: { order: 1, name: "Custom Games", players: 0, games: [] },
  };
  const defReg =
    window.settings &&
    window.settings.list &&
    window.settings.list.defaultRegion &&
    window.settings.list.defaultRegion.val;
  for (const tup of ww?.serverListData || []) {
    const regionID = tup[1];
    const meta = tup[4];
    if (!meta) continue;
    if (!servers[regionID]) {
      servers[regionID] = {
        name: config.regionNames[regionID],
        players: 0,
        games: [],
      };
      if (defReg === regionID) servers[regionID].order = 1;
    }
    const bucket = meta.cs ? "custom" : regionID;
    servers[bucket].games.push(tup);
    servers[bucket].players += tup[2];
  }
  const hB: ServerBucket[] = [];
  for (const k in servers)
    if (Object.prototype.hasOwnProperty.call(servers, k)) hB.push(servers[k]);
  hB.sort((a, b) => (b.order || -1) - (a.order || -1));
  const pick = hB[hj];
  if (!pick) return;
  const games = pick.games
    .filter((t: Krunker.ServerTuple) => t[2] < t[3])
    .sort((a: Krunker.ServerTuple, b: Krunker.ServerTuple) => b[2] - a[2]);
  if (!games.length) return;
  client.switchGame(games[0][0]);
}

Object.defineProperty(window, "quickJoinRegion", {
  get: () => quickJoinRegion,
});

function patchMapDataCapture(game: Krunker.Game): void {
  const map = game.map as AnyRecord | undefined;
  if (!map || map.__originalMapDataCapturePatched) return;

  const generate = map.generate;
  if (typeof generate === "function") {
    map.generate = function (this: AnyRecord, mapIndex: unknown) {
      rememberOriginalMapData(game, mapIndex);
      return generate.apply(this, arguments as any);
    };
  }

  const asyncGenerate = map.asyncGenerate;
  if (typeof asyncGenerate === "function") {
    map.asyncGenerate = function (this: AnyRecord, mapIndex: unknown) {
      rememberOriginalMapData(game, mapIndex);
      return asyncGenerate.apply(this, arguments as any);
    };
  }

  map.__originalMapDataCapturePatched = true;
}

// The compiled engine wraps everything in a webpack-style IIFE, so `var client`
// at src/index.js:133 lives in module scope — NOT on window. To capture the
// ClientManager instance we observe `ClientManager.PING_REGION_CACHE_KEY = ...`
// (src/index.js:132) — `this` at that assignment is the ClientManager class
// itself, giving us its prototype. We then install an accessor for
// `fetchGameInfo` on the prototype so the next instance method lookup
// (`client.fetchGameList()` runs at startup) leaks the instance via `this`.

Object.defineProperty(Object.prototype, "finishInit", {
  set(this: Krunker.Game, value: Krunker.Game["finishInit"]) {
    delete Object.prototype.finishInit;
    this.finishInit = value;
    doUnlockLevelRequirementHooks(this);
    patchMapDataCapture(this);
    const { init } = this;
    if (typeof init !== "function") return;
    this.init = function (
      this: Krunker.Game,
      ...args: Parameters<Krunker.Game["init"]>
    ) {
      patchMapDataCapture(this);
      return init.apply(this, args);
    };
  },
  configurable: true,
});

{
  const pr = Image.prototype;
  const src = Object.getOwnPropertyDescriptor(pr, "src");
  if (!src?.get || !src.set) throw new Error("Image.src descriptor missing");
  Object.defineProperty(pr, "src", {
    get: src.get,
    set: function (this: HTMLImageElement, value: string): void {
      const newValue = rewrite(value);
      // console.log(`image ${value} -> ${newValue}`);
      src.set.call(this, newValue);
    },
  });
}

{
  const pr = XMLHttpRequest.prototype;
  const { open } = pr;
  pr.open = function (
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    ...args: [
      async?: boolean,
      username?: string | null,
      password?: string | null,
    ]
  ): void {
    const newUrl = rewrite(String(url));
    // console.log(`xhr ${url} -> ${newUrl}`);
    return open.call(this, method, newUrl, ...args);
  } as XMLHttpRequest["open"];
}

{
  const pr = History.prototype;
  const { pushState, replaceState } = pr;
  pr.pushState = function (
    this: History,
    state: any,
    title: string,
    url?: string | URL | null,
  ): void {
    const newUrl = rewrite_history(url);
    // console.log(`pushState ${url} -> ${newUrl}`);
    return pushState.call(this, state, title, newUrl);
  };
  pr.replaceState = function (
    this: History,
    state: any,
    title: string,
    url?: string | URL | null,
  ): void {
    const newUrl = rewrite_history(url);
    if (url === "offline") return;
    // console.log(`replaceState ${url} -> ${newUrl}`);
    return replaceState.call(this, state, title, newUrl);
  };
}

function rewrite_history<T extends RewriteableHistoryUrl>(url: T): T {
  // console.log("url", url);
  return url;
}

{
  const pr = HTMLMediaElement.prototype;
  const src = Object.getOwnPropertyDescriptor(pr, "src");
  if (!src?.get || !src.set)
    throw new Error("HTMLMediaElement.src descriptor missing");
  Object.defineProperty(pr, "src", {
    get: src.get,
    set: function (this: HTMLMediaElement, value: string): void {
      const newValue = rewrite(value);
      // console.log(`media ${value} -> ${newValue}`);
      src.set.call(this, newValue);
    },
  });
}

{
  const WebSocket = window.WebSocket;
  const pr = WebSocket.prototype;
  // Hosts that are legacy/placeholder targets baked into the client and
  // must be redirected at the current page's origin. Anything else
  // (e.g. va.krunker.zip from the matchmaker's seek-game response in a
  // brain+nodes topology) MUST be honored verbatim, otherwise game WS
  // connections all collapse onto whichever host served the page.
  const WrappedWebSocket = function (
    this: WebSocket,
    value: string | URL,
    protocols?: string | string[],
  ): WebSocket {
    // const newValue = "ws://127.0.0.1:6767/ws";
    const o = new URL(String(value), location.href);
    const newValue =
      (location.protocol === "https:" ? "wss:" : "ws:") +
      "//" +
      location.host +
      o.pathname +
      o.search;
    const wss = new WebSocket(newValue, protocols as any);
    return wss;
  } as any;
  WrappedWebSocket.prototype = pr;
  WrappedWebSocket.CONNECTING = WebSocket.CONNECTING;
  WrappedWebSocket.OPEN = WebSocket.OPEN;
  WrappedWebSocket.CLOSING = WebSocket.CLOSING;
  WrappedWebSocket.CLOSED = WebSocket.CLOSED;
  window.WebSocket = WrappedWebSocket;
}

const xhrOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (
  this: XMLHttpRequest,
  method: string,
  url: string | URL,
  ...args: [async?: boolean, username?: string | null, password?: string | null]
): void {
  const rewrittenUrl = rewrite(url);
  // console.log("xhr", method, url, "=>", rewrittenUrl);
  return xhrOpen.call(this, method, rewrittenUrl as string | URL, ...args);
} as XMLHttpRequest["open"];

const nativeFetch = window.fetch;
window.fetch = function (this: Window, init: FetchInput, opts?: FetchInit) {
  if (typeof init === "string") {
    init = rewrite(init);
  } else if (init instanceof URL) {
    init = rewrite(init.toString());
  } else if (init instanceof Request) {
    init = new Request(rewrite(init.url) as string, init);
  } else console.log("unknown init", init);
  return nativeFetch.call(this, init, opts);
};
